import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { FileText, Save, Edit2, X, Home, Info, Users, BookOpen, Radio, FileQuestion, DollarSign, Mail } from 'lucide-react';
import { toast } from 'sonner';

const PagesManager = ({ onUpdate }) => {
  const [contentItems, setContentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    key: '',
    title_en: '',
    title_ti: '',
    content_en: '',
    content_ti: ''
  });

  const pageTemplates = [
    { key: 'home-welcome', label: 'Home - Welcome Section', icon: Home, color: 'bg-blue-600' },
    { key: 'about-intro', label: 'About - Introduction', icon: Info, color: 'bg-purple-600' },
    { key: 'history', label: 'About - Church History', icon: FileText, color: 'bg-amber-600' },
    { key: 'purpose', label: 'About - Our Purpose', icon: FileText, color: 'bg-green-600' },
    { key: 'vision', label: 'About - Our Vision', icon: FileText, color: 'bg-pink-600' },
    { key: 'youth', label: 'About - Youth Ministry', icon: Users, color: 'bg-indigo-600' },
    { key: 'ministries-intro', label: 'Ministries - Introduction', icon: Users, color: 'bg-teal-600' },
    { key: 'sermons-intro', label: 'Sermons - Page Introduction', icon: BookOpen, color: 'bg-orange-600' },
    { key: 'livestream-info', label: 'Live Stream - Information', icon: Radio, color: 'bg-red-600' },
    { key: 'resources-intro', label: 'Resources - Introduction', icon: FileQuestion, color: 'bg-cyan-600' },
    { key: 'building', label: 'Donate - Building Campaign', icon: DollarSign, color: 'bg-emerald-600' },
    { key: 'contact-info', label: 'Contact - Page Content', icon: Mail, color: 'bg-gray-600' }
  ];

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await api.get('/content');
      setContentItems(response.data);
    } catch (error) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (key) => {
    const item = contentItems.find(c => c.key === key);
    if (item) {
      setFormData({
        key: item.key,
        title_en: item.title_en,
        title_ti: item.title_ti,
        content_en: item.content_en,
        content_ti: item.content_ti
      });
      setEditingId(item.id);
    } else {
      // Create new content for this page
      const template = pageTemplates.find(p => p.key === key);
      setFormData({
        key: key,
        title_en: template?.label || '',
        title_ti: '',
        content_en: '',
        content_ti: ''
      });
      setEditingId('new');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId === 'new') {
        await api.post('/admin/content', formData);
        toast.success('Page content created!');
      } else {
        await api.put(`/admin/content/${editingId}`, formData);
        toast.success('Page content updated!');
      }
      resetForm();
      fetchContent();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to save content');
      console.error(error);
    }
  };

  const resetForm = () => {
    setFormData({
      key: '',
      title_en: '',
      title_ti: '',
      content_en: '',
      content_ti: ''
    });
    setEditingId(null);
  };

  const getContentByKey = (key) => {
    return contentItems.find(item => item.key === key);
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Pages Manager</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Select a page from the sidebar to edit its content
        </p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left Sidebar - Page List */}
        <div className="col-span-12 lg:col-span-3">
          <Card className="p-4">
            <h3 className="font-bold text-gray-900 dark:text-white mb-4">Website Pages</h3>
            <div className="space-y-1">
              {pageTemplates.map((template) => {
                const content = getContentByKey(template.key);
                const Icon = template.icon;
                const isSelected = editingId && formData.key === template.key;
                
                return (
                  <button
                    key={template.key}
                    onClick={() => handleEdit(template.key)}
                    className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors ${
                      isSelected
                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium">{template.label.split(' - ')[0]}</p>
                      <p className="text-xs text-gray-500">{template.label.split(' - ')[1]}</p>
                    </div>
                    {content ? (
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    ) : (
                      <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                    )}
                  </button>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Right Side - Content Editor */}
        <div className="col-span-12 lg:col-span-9">
          {editingId ? (
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {pageTemplates.find(p => p.key === formData.key)?.label}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">Key: {formData.key}</p>
                </div>
                <Button onClick={resetForm} variant="outline" size="sm">
                  <X className="w-4 h-4 mr-2" />
                  Close
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Title (English)</label>
                    <Input
                      value={formData.title_en}
                      onChange={(e) => setFormData({...formData, title_en: e.target.value})}
                      required
                      placeholder="Enter title in English"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Title (Tigrinya)</label>
                    <Input
                      value={formData.title_ti}
                      onChange={(e) => setFormData({...formData, title_ti: e.target.value})}
                      required
                      placeholder="ትግርኛ ርእሲ ኣእትዉ"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Content (English)</label>
                  <textarea
                    className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600 font-mono text-sm"
                    value={formData.content_en}
                    onChange={(e) => setFormData({...formData, content_en: e.target.value})}
                    required
                    rows="12"
                    placeholder="Enter content in English..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.content_en.length} characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Content (Tigrinya)</label>
                  <textarea
                    className="w-full p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600 font-mono text-sm"
                    value={formData.content_ti}
                    onChange={(e) => setFormData({...formData, content_ti: e.target.value})}
                    required
                    rows="12"
                    placeholder="ትግርኛ ትሕዝቶ ኣእትዉ..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.content_ti.length} characters
                  </p>
                </div>

                <div className="flex space-x-3">
                  <Button type="submit" className="bg-green-600 hover:bg-green-700">
                    <Save className="w-4 h-4 mr-2" />
                    {editingId === 'new' ? 'Create Content' : 'Save Changes'}
                  </Button>
                  <Button type="button" onClick={resetForm} variant="outline">
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          ) : (
            <Card className="p-12 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Select a Page to Edit
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Choose a page from the left sidebar to view and edit its content
              </p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default PagesManager;
